import React, {type ComponentType, useMemo} from 'react';

import cs from 'classnames';

import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {AttachTooltip} from 'app/general/overlays';
import StateButton from 'app/general/widgets/StateButton';
import {isMemberTeam} from 'app/teams/selectors';
import {gt} from 'app/utils/i18n';

import type {
  ContentType,
  TeamMembershipSerializedType,
  TeamSerializedType,
} from 'sweet-types';

type BaseObject = {
  team_memberships: Array<TeamMembershipSerializedType>;
  html_url: string;
};
export type ListItemProps<T> = {
  object: T;
  team: TeamSerializedType | null;
  remove: (team: TeamSerializedType, item: T) => Promise<any>;
  refetchList: () => Promise<any>;
};
export type Props<T> = ListItemProps<T> & {
  contentType: 'procedure' | 'process' | 'policy' | 'quiz' | 'document';
  ListItem: ComponentType<{
    object: T;
    refreshData: () => Promise<unknown>;
    toggleRelation: () => void;
    activeRelations: Record<string, boolean> | Record<ContentType, boolean>;
    enableBulkSelect: boolean;
    isSelected: boolean;
    addToSelection: (doc: T) => void;
    removeFromSelection: (doc: T) => void;
  }>;
};

export const TeamListItem = <T extends BaseObject>({
  team,
  contentType,
  object,
  ListItem,
  refetchList,
  remove,
}: Props<T>) => {
  const membership = useMemo(
    () => isMemberTeam(object.team_memberships, team),
    [object, team]
  );

  return (
    <div className="row" key={object.html_url}>
      <div className="col">
        <ListItem
          object={object}
          refreshData={refetchList}
          toggleRelation={() => null}
          activeRelations={{}}
          enableBulkSelect={false}
          isSelected={false}
          addToSelection={() => null}
          removeFromSelection={() => null}
        />
      </div>
      <div className="col-auto border-bottom d-flex align-items-center">
        <div className="py-1">
          {team && team.can_edit ? (
            <RemoveFromTeamButton
              iconClassName="text-dark"
              contentType={contentType}
              membership={membership}
              onRemove={() => {
                return remove(team, object);
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

interface RemoveFromTeamButtonProps<T> {
  membership: TeamMembershipSerializedType;
  contentType: Props<T>['contentType'];
  onRemove: () => Promise<unknown>;
  iconClassName: string;
}

export function RemoveFromTeamButton<T extends BaseObject>({
  iconClassName,
  contentType,
  membership,
  onRemove,
}: RemoveFromTeamButtonProps<T>) {
  if (membership == null) {
    // not a member of the team.
    return null;
  }

  return (
    <AttachTooltip
      tooltipContent={
        membership.direct ? (
          <div>
            <div className="mb1">{gt('Remove from team')}</div>
            <div className="small">
              {gt(
                'This will remove the team from all of the tasks related to this %(contentType)s',
                {
                  contentType,
                }
              )}
            </div>
          </div>
        ) : (
          <div>
            <div className="mb1">
              <i
                className={cs('far mr-1', {
                  'fa-copy': membership.via_process,
                  'fa-folder': !membership.via_process,
                })}
              />
              {membership.via_name}
            </div>
            <div className="small">
              {membership.via_process
                ? gt(
                    'This %(contentType)s was added to the team via a process',
                    {
                      contentType,
                    }
                  )
                : gt(
                    'This %(contentType)s was added to the team via a folder',
                    {
                      contentType,
                    }
                  )}
            </div>
          </div>
        )
      }
    >
      {(handlers) => (
        <span {...handlers}>
          {membership.direct ? (
            <StateButton
              className={cs('btn btn-link py-0 px-1', iconClassName)}
              onClick={onRemove}
            >
              <i className="fa fa-fw fa-times" />
            </StateButton>
          ) : (
            <FullHTMLLink
              html_url={membership.html_url}
              className={cs('btn btn-link py-0 px-1', iconClassName)}
            >
              <i
                className={cs('far fa-fw', {
                  'fa-copy': membership.via_process,
                  'fa-folder': !membership.via_process,
                })}
              />
            </FullHTMLLink>
          )}
        </span>
      )}
    </AttachTooltip>
  );
}
